import { useState } from "react"
import { isFilled, Content, asText, asLink } from "@prismicio/client"
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next"
import Grid from "@aura-design/system/grid"
import Modal from "@aura-design/system/modal"
import { EnterFullScreenIcon } from "@radix-ui/react-icons"
import {
  XIcon,
  LinkedinIconPage,
  InstagramIconPage,
  FacebookIconPage,
  YouTubeIconPage,
  WhatsAppIconPage,
  TikTokIconPage,
  PinterestIconPage,
} from "@airplan/ui/icons"
import { normalizeUrl, getDisplayUrl } from "@airplan/utils/data-view"

import { PrismicRichText } from "./prismic-richtext"

type CollectionStorefrontyProps = {
  doc: Content.StorefrontDocument
}

const CollectionStorefront = ({
  doc,
  ...props
}: CollectionStorefrontyProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const iconsResolver = {
    twitter: XIcon,
    facebook: FacebookIconPage,
    youtube: YouTubeIconPage,
    instagram: InstagramIconPage,
    linkedin: LinkedinIconPage,
    whatsapp: WhatsAppIconPage,
    tiktok: TikTokIconPage,
    pinterest: PinterestIconPage,
  }

  return (
    <div className="text-center mb-5 group" {...props}>
      {isFilled.richText(doc.data.content) ? (
        <div className="shadow-lg p-0.5 mb-3 bg-white rounded relative zoom h-15">
          <EnterFullScreenIcon className="icon absolute right-1 top-1 hidden group-hover:flex" />
          <button
            onClick={() => setIsOpen(true)}
            className="unset cursor-pointer"
          >
            {isFilled.image(doc.data.logo) ? (
              <PrismicNextImage
                field={doc.data.logo}
                fill
                className="object-contain"
                fallbackAlt=""
              />
            ) : (
              <span className="h3 font-bold action">
                {asText(doc.data.title)}
              </span>
            )}
          </button>
        </div>
      ) : (
        <div className="shadow-lg p-0.5 mb-2 h-15 bg-white rounded relative">
          {isFilled.image(doc.data.logo) ? (
            <PrismicNextImage
              field={doc.data.logo}
              fill
              className="object-contain"
              alt=""
            />
          ) : (
            <span className="h3 font-bold action">
              {asText(doc.data.title)}
            </span>
          )}
        </div>
      )}

      <div className="text-dark-blue">
        {isFilled.richText(doc.data.title) && (
          <div>
            <span className="h6 font-bold">{asText(doc.data.title)}</span>
          </div>
        )}
        {isFilled.keyText(doc.data.subcategory) && (
          <div>
            <span>{doc.data.subcategory}</span>
          </div>
        )}
        {isFilled.select(doc.data.floor) && (
          <div className="mt-1">
            <span>{doc.data.floor}</span>
          </div>
        )}
      </div>
      <Modal
        classNameModDetail="w-[90vw] h-[90vh]"
        isVisible={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <Grid
          col={isFilled.image(doc.data.photo) ? "two" : "one"}
          className="h-full text-dark-blue"
        >
          {isFilled.image(doc.data.photo) && (
            <div className="relative md:h-full">
              <PrismicNextImage
                field={doc.data.photo}
                className="object-cover hide-small"
                alt=""
                fill
              />
              <PrismicNextImage
                field={doc.data.photo}
                className="object-contain hide-medium hide-large"
                alt=""
              />
            </div>
          )}
          <div className="valign">
            <div>
              <div className="relative">
                {isFilled.image(doc.data.logo) ? (
                  <PrismicNextImage
                    field={doc.data.logo}
                    className="object-contain"
                    width={300}
                    alt=""
                    imgixParams={{
                      txt: doc.data.logo?.copyright,
                      txtAlign: ["bottom", "center"],
                      txtSize: 10,
                    }}
                  />
                ) : (
                  <span className="h3 font-bold">{asText(doc.data.title)}</span>
                )}
              </div>
              <div>
                {isFilled.richText(doc.data.title) && (
                  <div>
                    <span className="h6 font-bold">
                      {asText(doc.data.title)}
                    </span>
                  </div>
                )}
                {isFilled.keyText(doc.data.subcategory) && (
                  <div>
                    <span>{doc.data.subcategory}</span>
                  </div>
                )}
                {isFilled.select(doc.data.floor) && (
                  <div className="mt-1">
                    <span>{doc.data.floor}</span>
                  </div>
                )}
              </div>
              {isFilled.link(doc.data.web_site) && (
                <div>
                  <a
                    href={normalizeUrl(asLink(doc.data.web_site))}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline"
                  >
                    {getDisplayUrl(asLink(doc.data.web_site))}
                  </a>
                </div>
              )}
              <div>
                {isFilled.richText(doc.data.content) && (
                  <PrismicRichText field={doc.data.content} />
                )}
              </div>
              <div>
                {doc.data.floors.map((item, index) => (
                  <div  key={index}>
                    <span>{item.floor}</span>
                  </div>
                ))}
              </div>
              <br />
              <div>
                {doc.data.zones.map((item, index) => (
                  <div key={index}>
                    <span > {item.zone}</span>
                  </div>
                ))}
              </div>
              <div>
                {doc.data.social_media?.length > 0 ? (
                  <ul className="nav-list justify-center gap-1">
                    {doc.data.social_media?.map((sub_item) => {
                      const SocialIcon = sub_item.icon
                        ? iconsResolver[sub_item.icon]
                        : null

                      if (!SocialIcon) {
                        return null
                      }

                      return (
                        <li
                          className={"circle-icon my-1 "}
                          key={JSON.stringify(sub_item)}
                        >
                          <a
                            href={normalizeUrl(asLink(sub_item.label_link))}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {SocialIcon ? (
                              <SocialIcon className="icon" />
                            ) : null}
                          </a>
                        </li>
                      )
                    })}
                  </ul>
                ) : null}
              </div>
            </div>
          </div>
        </Grid>
      </Modal>
    </div>
  )
}

export default CollectionStorefront
